import React, { useEffect, useId, useRef } from 'react';
import './ConfirmDialog.css';

/**
 * The workspace confirmation dialog - the same `settings-confirm-modal` shell the
 * settings screens already use, with the keyboard contract in one place: focus
 * moves into the dialog on open, Escape cancels, and focus returns to whatever
 * opened it on close.
 *
 * `description` is the one-line consequence; `children` is room for the detail
 * that consequence needs (a list, a warning, the records that block the action).
 *
 * Pass `triggerRef` holding the control that opened the dialog. Safari and Firefox
 * do not focus a button on click, so the active element is not a reliable record of
 * what to hand focus back to.
 */
const ConfirmDialog = ({
  open,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  busy = false,
  confirmDisabled = false,
  className = '',
  triggerRef = null,
  confirmClassName = 'primary-btn',
  cancelClassName = 'secondary-btn',
}) => {
  const dialogRef = useRef(null);
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descriptionId = `${baseId}-description`;

  useEffect(() => {
    if (!open) return undefined;
    const trigger = triggerRef?.current || document.activeElement;
    dialogRef.current?.focus();
    return () => {
      // The trigger is gone whenever the confirmed action reshaped the list it
      // lived in, so there is nothing to restore focus to in that case.
      if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the trigger is read once, when the dialog opens.
  }, [open]);

  // Capture, and marked handled, so the Escape that closes this dialog is not
  // also taken by a sheet or dialog underneath it (they skip a defaultPrevented
  // Escape, as Modal does).
  useEffect(() => {
    if (!open || busy) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onCancel?.();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [busy, onCancel, open]);

  if (!open) return null;

  return (
    <div className="settings-modal-backdrop" role="presentation">
      <div
        ref={dialogRef}
        className={`settings-confirm-modal ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex="-1"
      >
        <h2 id={titleId}>{title}</h2>
        {description && <p id={descriptionId}>{description}</p>}
        {children}
        <div className="confirm-dialog__actions">
          <button
            type="button"
            className={confirmClassName}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {confirmLabel}
          </button>
          <button type="button" className={cancelClassName} onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
